import React from 'react';
import styled from 'styled-components';
import { Trans, t } from '@lingui/macro';
import type { DelegateUploadAck } from '../types';
import { IconList, type IconListItem } from './IconList';
import { StatusBox } from './StatusBox';
import {
  CloseCircleGlyph,
  InvalidCircleGlyph,
  LoadingGlyph,
  TotalRecordsGlyph,
  ValidCircleGlyph,
} from './glyphs';
import { MOBILE, tok } from './styles';

/**
 * Post-upload result view (PRD §2 phase 3 / §3): the "File Upload summary"
 * heading, an intro line, the totals icon list (total / valid / invalid) and
 * the conditional status tiles (valid → Processing, invalid → Excluded +
 * Download error report). UI is driven off the counts (O-12).
 */
const H2 = styled.h2`
  margin: 0 0 16px;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  color: ${({ theme }) => tok(theme, 'general_font_primary', '#1E1E1E')};
`;

const Intro = styled.p`
  margin: 0 0 24px;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => tok(theme, 'general_font_primary', '#1E1E1E')};
`;

const Totals = styled.div`
  /* The source indents the totals list ~92px from the summary text. */
  margin: 0 0 32px 92px;

  @media (max-width: ${MOBILE}) {
    margin-left: 0;
  }
`;

const Tiles = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 24px;

  @media (max-width: ${MOBILE}) {
    flex-direction: column;
  }
`;

export type ResultSummaryProps = {
  result: DelegateUploadAck;
  /** Whether the response carried a downloadable error report. */
  hasErrorReport: boolean;
  onDownloadErrorReport: () => void;
};

export const ResultSummary: React.FC<ResultSummaryProps> = ({
  result,
  hasErrorReport,
  onDownloadErrorReport,
}) => {
  const { totalLinesCount, validLinesCount, wrongValidationCount } = result;

  const totals: IconListItem[] = [
    {
      icon: <TotalRecordsGlyph size={20} color="#009EDB" />,
      title: t`${totalLinesCount} total records`,
      description: <Trans>Total number of records uploaded from the file.</Trans>,
    },
    {
      icon: <ValidCircleGlyph size={20} color="#00FF00" />,
      title: t`${validLinesCount} valid records`,
      description: <Trans>Records that passed initial validation and are being processed.</Trans>,
    },
    {
      icon: <InvalidCircleGlyph size={20} color="#FF0000" />,
      title: t`${wrongValidationCount} invalid records`,
      description: <Trans>Records that did not meet the criteria and were excluded.</Trans>,
    },
  ];

  return (
    <div>
      <H2>
        <Trans>File Upload summary</Trans>
      </H2>
      <Intro>
        <Trans>
          File uploaded successfully. Below is a summary of the initial validation and processing
          status:
        </Trans>
      </Intro>
      <Totals>
        <IconList items={totals} />
      </Totals>
      <Tiles>
        {validLinesCount > 0 && (
          <StatusBox
            testId="un-bulk-upload-tile-valid"
            tone="warning"
            iconNode={<LoadingGlyph size={24} color="#1E1E1E" />}
            chipLabel={t`Processing`}
            title={<Trans>Valid records Processing</Trans>}
            description={
              <Trans>
                The valid records are now being processed. A confirmation email will be sent once the
                processing is complete.
              </Trans>
            }
          />
        )}
        {wrongValidationCount > 0 && (
          <StatusBox
            testId="un-bulk-upload-tile-invalid"
            tone="danger"
            iconNode={<CloseCircleGlyph size={24} color="#1E1E1E" />}
            chipLabel={t`Excluded`}
            title={<Trans>Invalid records</Trans>}
            description={
              <Trans>
                Some records did not meet the required criteria and were not sent for processing.
              </Trans>
            }
            action={
              hasErrorReport
                ? { label: <Trans>Download error report</Trans>, onClick: onDownloadErrorReport }
                : undefined
            }
          />
        )}
      </Tiles>
    </div>
  );
};
